import React from "react";
import { Badge, Box, Flex, Icon, Image, SimpleGrid, Stack, Text, useColorModeValue } from "@chakra-ui/react";
import { MdDescription } from "react-icons/md";
import { detectFileType, getDocumentRoleLabel } from "./certificationUtils";

/**
 * Normalizes the two possible document inputs into a uniform [{url, role}] list.
 * @param {Array<{url:string,role:?string}>} [documents] - Preferred input
 * @param {Array<string>} [documentUrls] - Legacy input
 * @returns {Array<{url:string,role:?string}>}
 */
function normalizeDocuments(documents, documentUrls) {
    if (Array.isArray(documents) && documents.length > 0) return documents;
    if (Array.isArray(documentUrls)) return documentUrls.map(url => ({ url, role: null }));
    return [];
}

/**
 * Empty state shown when no documents are attached to the certification.
 * @returns {React.ReactElement}
 */
function EmptyState() {
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return <Text color={color} fontSize="sm" textAlign="center" py="32px">Aucun document disponible</Text>;
}

/**
 * Renders a small badge with the KYC role label, or nothing when role is null.
 * @param {Object} props - { role: ?string }
 * @returns {React.ReactElement|null}
 */
function RoleBadge({ role }) {
    const cfg = getDocumentRoleLabel(role);
    if (!cfg) return null;
    return (
        <Badge alignSelf="flex-start" bg={`${cfg.color}1f`} color={cfg.color}
            border={`1px solid ${cfg.color}55`} borderRadius="6px" px="6px" py="2px"
            fontSize="0.65rem" fontWeight="600" textTransform="none">
            {cfg.label}
        </Badge>
    );
}

/**
 * Clickable image preview thumbnail.
 * @param {Object} props
 * @param {string} props.url - Image URL
 * @param {number} props.index - Document index
 * @param {Function} props.onClick - Click handler receiving the URL
 * @returns {React.ReactElement}
 */
function ImageThumbnail({ url, index, onClick }) {
    const border = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    return (
        <Box onClick={() => onClick(url)} cursor="pointer" borderRadius="12px" overflow="hidden"
            border="1px solid" borderColor={border} transition="all 0.2s ease"
            _hover={{ transform: "scale(1.02)", boxShadow: "lg" }}>
            <Image src={url} alt={`Document ${index + 1}`} w="100%" h="auto" display="block" />
        </Box>
    );
}

/**
 * Clickable PDF document placeholder thumbnail.
 * @param {Object} props
 * @param {string} props.url - PDF URL
 * @param {number} props.index - Document index
 * @param {Function} props.onClick - Click handler receiving the URL
 * @returns {React.ReactElement}
 */
function PdfThumbnail({ url, index, onClick }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const border = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    const color = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex onClick={() => onClick(url)} cursor="pointer" p="24px" borderRadius="12px"
            border="1px solid" borderColor={border} bg={bg} direction="column" align="center" gap="8px"
            transition="all 0.2s ease" _hover={{ transform: "translateY(-2px)" }}>
            <Icon as={MdDescription} w="48px" h="48px" color={color} />
            <Text color={color} fontSize="sm" fontWeight="600">Document PDF {index + 1}</Text>
        </Flex>
    );
}

/**
 * Wraps a thumbnail with its optional KYC role badge underneath.
 * @param {Object} props
 * @param {string} props.url - Document URL
 * @param {?string} props.role - KYC role or null
 * @param {number} props.index - Document index for labeling
 * @param {Function} props.onImageClick - Click handler receiving the URL
 * @returns {React.ReactElement}
 */
function DocumentCard({ url, role, index, onImageClick }) {
    const isImage = detectFileType(url) === "image";
    return (
        <Stack spacing="6px">
            {isImage
                ? <ImageThumbnail url={url} index={index} onClick={onImageClick} />
                : <PdfThumbnail url={url} index={index} onClick={onImageClick} />}
            <RoleBadge role={role} />
        </Stack>
    );
}

/**
 * Grid display of certification document thumbnails (images and PDFs) with KYC role badges.
 * Accepts either the new `documents` array of {url, role} or the legacy `documentUrls`
 * array of strings (mapped to {url, role: null}).
 * @param {Object} props
 * @param {Array<{url:string,role:?string}>} [props.documents] - Documents with KYC role (preferred)
 * @param {Array<string>} [props.documentUrls] - Legacy flat URL list (fallback)
 * @param {Function} props.onImageClick - Callback when a document is clicked
 * @returns {React.ReactElement}
 */
function DocumentsGrid({ documents, documentUrls, onImageClick }) {
    const items = normalizeDocuments(documents, documentUrls);
    if (!items.length) return <EmptyState />;
    return (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px">
            {items.map((item, index) => (
                <DocumentCard key={index} url={item.url} role={item.role}
                    index={index} onImageClick={onImageClick} />
            ))}
        </SimpleGrid>
    );
}

export default DocumentsGrid;
